import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, ShoppingCart, Users, Package, TrendingUp, Clock, Truck, CheckCircle, ArrowRight, AlertTriangle, BarChart3, Mail, Tag, RefreshCw, Webhook, Loader2, ExternalLink, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ icon, label, value, sub, color = 'cyan' }) => {
  const colors = {
    cyan: 'bg-[#00F0FF]/10 text-[#00F0FF]',
    green: 'bg-green-500/10 text-green-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    purple: 'bg-purple-500/10 text-purple-400',
    blue: 'bg-blue-500/10 text-blue-400',
  };
  return (
    <div className="glass-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
        {sub && <span className="text-xs text-[#A1A7B5]">{sub}</span>}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-[#A1A7B5] mt-1">{label}</p>
    </div>
  );
};

const ShipStationCard = () => {
  const [webhooks, setWebhooks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [syncingProducts, setSyncingProducts] = useState(false);

  const handleSyncProducts = async () => {
    setSyncingProducts(true);
    try {
      const { data } = await axios.post(`${API}/admin/shipstation/sync-products`, {}, { withCredentials: true });
      toast.success(data.message || 'Products synced to ShipStation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync products to ShipStation');
    } finally {
      setSyncingProducts(false);
    }
  };

  const backendHost = (() => {
    try { return new URL(process.env.REACT_APP_BACKEND_URL).host; } catch { return ''; }
  })();

  const fetchWebhooks = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/admin/shipstation/webhooks`, { withCredentials: true });
      setWebhooks(data.webhooks || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reach ShipStation API');
      setWebhooks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWebhooks(); }, []);

  const shipNotify = (webhooks || []).find(
    (w) => w.event === 'SHIP_NOTIFY' && (w.url || '').includes(backendHost)
  );
  const connected = Boolean(shipNotify);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const webhookUrl = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/webhook/shipstation`;
      await axios.post(`${API}/admin/shipstation/subscribe-webhook`, { webhook_url: webhookUrl }, { withCredentials: true });
      toast.success('ShipStation webhook connected — shipped orders will now auto-email customers with tracking');
      await fetchWebhooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect ShipStation webhook');
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="glass-card p-6 mb-8" data-testid="shipstation-integration-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
            <Webhook className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              ShipStation Auto-Sync
            </h2>
            {loading ? (
              <p className="text-[#A1A7B5] text-xs">Checking connection…</p>
            ) : error ? (
              <p className="text-red-400 text-xs" data-testid="shipstation-status-error">{error}</p>
            ) : connected ? (
              <p className="text-green-400 text-xs flex items-center gap-1" data-testid="shipstation-status-connected">
                <CheckCircle className="w-3.5 h-3.5" /> Connected — orders shipped in ShipStation auto-email customers
              </p>
            ) : (
              <p className="text-[#A1A7B5] text-xs" data-testid="shipstation-status-disconnected">
                Not connected — click to enable automatic shipped-order emails with tracking
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchWebhooks}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#111621] border border-[#1E2532] hover:border-[#00F0FF]/40 text-[#A1A7B5] text-xs font-medium transition-colors disabled:opacity-50"
            data-testid="shipstation-refresh-btn"
            aria-label="Refresh ShipStation status"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleConnect}
            disabled={connecting}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 ${
              connected
                ? 'bg-[#111621] border border-[#1E2532] text-[#00F0FF] hover:border-[#00F0FF]/40'
                : 'bg-[#00F0FF] text-[#0C0E14] hover:bg-[#00F0FF]/90'
            }`}
            data-testid="shipstation-connect-btn"
          >
            {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Webhook className="w-4 h-4" />}
            {connecting ? 'Connecting…' : connected ? 'Reconnect Webhook' : 'Connect Webhook'}
          </button>
          <button
            onClick={handleSyncProducts}
            disabled={syncingProducts}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-[#111621] border border-[#1E2532] text-purple-300 hover:border-purple-400/50 transition-colors disabled:opacity-50"
            data-testid="shipstation-sync-products-btn"
          >
            {syncingProducts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
            {syncingProducts ? 'Syncing…' : 'Sync Products'}
          </button>
        </div>
      </div>
      {connected && shipNotify?.url && (
        <div className="mt-4 flex items-center gap-2 text-xs text-[#A1A7B5] bg-[#111621] rounded-lg px-3 py-2" data-testid="shipstation-webhook-url">
          <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-mono truncate">{shipNotify.url}</span>
        </div>
      )}
    </div>
  );
};

const AfterShipCard = () => {
  const [polling, setPolling] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handlePoll = async () => {
    setPolling(true);
    try {
      const { data } = await axios.post(`${API}/admin/aftership/poll-now`, {}, { withCredentials: true });
      setLastResult(data);
      toast.success(data.message || 'Delivery check complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check deliveries');
    } finally {
      setPolling(false);
    }
  };

  return (
    <div className="glass-card p-6 mb-8" data-testid="aftership-integration-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Delivery Auto-Detection (AfterShip)
            </h2>
            <p className="text-[#A1A7B5] text-xs">
              Shipped orders are checked automatically every 2 hours and marked <span className="text-green-400">Delivered</span> when the carrier confirms — no webhook setup needed.
            </p>
            {lastResult && (
              <p className="text-[#00F0FF] text-xs mt-1" data-testid="aftership-last-result">
                Last check: {lastResult.checked} shipped order(s) · {lastResult.delivered} newly delivered
              </p>
            )}
          </div>
        </div>
        <button
          onClick={handlePoll}
          disabled={polling}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-[#00F0FF] text-[#0C0E14] hover:bg-[#00F0FF]/90 transition-colors disabled:opacity-50 shrink-0"
          data-testid="aftership-poll-btn"
        >
          {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {polling ? 'Checking…' : 'Check Deliveries Now'}
        </button>
      </div>
    </div>
  );
};

const AdminDashboardPage = () => {
  return <AdminDashboardInner />;
};

const SyncImagesCard = () => {
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/products/sync-vial-artwork`, {}, { withCredentials: true });
      setResult(data);
      toast.success(data.message || 'Catalog images synced');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync images');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="glass-card p-6 mb-8" data-testid="sync-images-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Package className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sync Catalog Images
            </h2>
            <p className="text-[#A1A7B5] text-xs max-w-xl">
              Applies the RENEW LABS vial artwork to every product in <span className="text-white">this environment&apos;s</span> database. Run this once after a deploy so production shows the latest images.
            </p>
            {result && (
              <p className="text-[#00F0FF] text-xs mt-1" data-testid="sync-images-result">
                {result.updated} updated · {result.unchanged} already correct
                {result.not_found?.length ? ` · ${result.not_found.length} not found` : ''}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={sync}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-[#00F0FF] text-[#0C0E14] hover:bg-[#00F0FF]/90 transition-colors disabled:opacity-50 shrink-0"
          data-testid="sync-images-btn"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {syncing ? 'Syncing…' : 'Sync Catalog Images'}
        </button>
      </div>
    </div>
  );
};

const AdminDashboardInner = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteLocked, setSiteLocked] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [now, setNow] = useState(Date.now());

  const fetchDashboard = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [dashRes, statusRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { withCredentials: true }),
        axios.get(`${API}/site-status`)
      ]);
      setData(dashRes.data);
      setSiteLocked(statusRes.data.locked);
      setLastUpdated(Date.now());
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      if (!silent) toast.error('Failed to refresh');
    } finally {
      if (!silent) setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard(true);
  }, []);

  // Tick every second to update the "X ago" label without re-fetching data
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const formatAgo = (ts) => {
    if (!ts) return '—';
    const sec = Math.floor((now - ts) / 1000);
    if (sec < 5) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    return `${hr}h ago`;
  };

  const toggleSiteLock = async () => {
    setToggling(true);
    try {
      const { data: res } = await axios.post(`${API}/admin/toggle-site-lock`, {}, { withCredentials: true });
      setSiteLocked(res.locked);
      toast.success(res.locked ? 'Site locked — Coming Soon page is live' : 'Site unlocked — Store is live');
    } catch {
      toast.error('Failed to toggle site lock');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-[#A1A7B5]">Failed to load dashboard data.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-[#A1A7B5] mt-1">Welcome back, {user?.name || 'Admin'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2" data-testid="dashboard-refresh-block">
              <span className="text-xs text-[#A1A7B5]" data-testid="dashboard-last-updated">
                Updated <span className="text-white">{formatAgo(lastUpdated)}</span>
              </span>
              <button
                onClick={() => fetchDashboard(false)}
                disabled={refreshing}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#111621] border border-[#1E2532] hover:border-[#00F0FF]/40 text-[#00F0FF] text-xs font-medium transition-colors disabled:opacity-50"
                data-testid="dashboard-refresh-btn"
                aria-label="Refresh dashboard"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
            <div className="flex items-center gap-3" data-testid="site-lock-toggle">
              <span className={`text-sm font-medium ${siteLocked ? 'text-yellow-400' : 'text-green-400'}`}>
                {siteLocked ? 'Coming Soon' : 'Store Live'}
              </span>
              <button
                onClick={toggleSiteLock}
                disabled={toggling}
                className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${siteLocked ? 'bg-yellow-500' : 'bg-green-500'} ${toggling ? 'opacity-50' : ''}`}
                aria-label="Toggle site lock"
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${siteLocked ? 'left-0.5' : 'left-[30px]'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard
            icon={<DollarSign className="w-6 h-6" />}
            label="Total Revenue"
            value={`$${data.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            color="green"
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            label="Total Profit"
            value={`$${(data.total_profit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            color="cyan"
          />
          <StatCard
            icon={<BarChart3 className="w-6 h-6" />}
            label="Profit Margin"
            value={`${data.profit_margin || 0}%`}
            color="blue"
          />
          <StatCard
            icon={<ShoppingCart className="w-6 h-6" />}
            label="Total Orders"
            value={data.total_orders + (data.pending_verification_orders || 0)}
            sub={`${data.pending_verification_orders || 0} to verify`}
            color="cyan"
          />
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label="Customers"
            value={data.total_users}
            sub={`${data.guest_orders} guest`}
            color="purple"
          />
          <StatCard
            icon={<Package className="w-6 h-6" />}
            label="Products"
            value={data.total_products}
            color="yellow"
          />
        </div>

        {/* Order Status Breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4 flex items-center space-x-3">
            <Clock className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="text-white font-semibold">{data.processing_orders}</p>
              <p className="text-xs text-[#A1A7B5]">Processing</p>
            </div>
          </div>
          <div className="glass-card p-4 flex items-center space-x-3">
            <Truck className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-white font-semibold">{data.shipped_orders}</p>
              <p className="text-xs text-[#A1A7B5]">Shipped</p>
            </div>
          </div>
          <div className="glass-card p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-white font-semibold">{data.delivered_orders}</p>
              <p className="text-xs text-[#A1A7B5]">Delivered</p>
            </div>
          </div>
          <Link to="/admin/orders" className="glass-card p-4 flex items-center space-x-3 hover:bg-[#111621] transition-colors" data-testid="dashboard-needs-verification-card">
            <ShieldAlert className={`w-5 h-5 ${(data.pending_verification_orders || 0) > 0 ? 'text-orange-400' : 'text-[#A1A7B5]'}`} />
            <div>
              <p className="text-white font-semibold">{data.pending_verification_orders || 0}</p>
              <p className="text-xs text-[#A1A7B5]">Needs Verification</p>
            </div>
          </Link>
        </div>

        {/* ShipStation Integration */}
        <ShipStationCard />

        {/* AfterShip Delivery Auto-Detection */}
        <AfterShipCard />

        {/* Sync Catalog Images */}
        <SyncImagesCard />

        {/* Low Stock Alert */}
        {data.low_stock_products && data.low_stock_products.length > 0 && (
          <div className="glass-card p-6 mb-8 border border-yellow-500/30" data-testid="low-stock-alert">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Low Stock Alert
                  </h2>
                  <p className="text-[#A1A7B5] text-xs">{data.low_stock_products.length} product{data.low_stock_products.length > 1 ? 's' : ''} below 10 units</p>
                </div>
              </div>
              <Link to="/admin/products" className="text-[#00F0FF] text-sm hover:underline">
                Manage Stock
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.low_stock_products.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3 bg-[#111621] rounded-lg" data-testid={`low-stock-${p.id}`}>
                  <span className="text-white text-sm truncate mr-3">{p.name}</span>
                  <span className={`text-sm font-bold flex-shrink-0 ${p.stock <= 3 ? 'text-red-400' : 'text-yellow-400'}`}>
                    {p.stock} left
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Orders */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Recent Orders
              </h2>
              <Link to="/admin/orders" className="text-[#00F0FF] text-sm hover:underline flex items-center space-x-1">
                <span>View All</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-3">
              {data.recent_orders.length === 0 ? (
                <p className="text-[#A1A7B5] text-center py-8">No orders yet</p>
              ) : (
                data.recent_orders.slice(0, 5).map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-[#111621] rounded-lg" data-testid={`recent-order-${order.id}`}>
                    <div>
                      <p className="text-white text-sm font-medium">
                        {order.guest_email || 'Registered User'}
                      </p>
                      <p className="text-[#A1A7B5] text-xs">
                        {new Date(order.created_at).toLocaleDateString()} - {order.items?.length || 0} item(s)
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-semibold">${order.total?.toFixed(2)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                        order.status === 'shipped' ? 'bg-blue-500/20 text-blue-400' :
                        order.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-[#1E2532] text-[#A1A7B5]'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-semibold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Quick Actions
            </h2>
            <div className="space-y-3">
              <Link to="/admin/products" className="flex items-center justify-between p-4 bg-[#111621] rounded-lg hover:bg-[#1E2532] transition-colors" data-testid="admin-link-products">
                <div className="flex items-center space-x-3">
                  <Package className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white">Manage Products</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A1A7B5]" />
              </Link>
              <Link to="/admin/analytics" className="flex items-center justify-between p-4 bg-[#111621] rounded-lg hover:bg-[#1E2532] transition-colors" data-testid="admin-link-analytics">
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white">Sales Analytics</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A1A7B5]" />
              </Link>
              <Link to="/admin/orders" className="flex items-center justify-between p-4 bg-[#111621] rounded-lg hover:bg-[#1E2532] transition-colors" data-testid="admin-link-orders">
                <div className="flex items-center space-x-3">
                  <ShoppingCart className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white">Manage Orders</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A1A7B5]" />
              </Link>
              <Link to="/admin/customers" className="flex items-center justify-between p-4 bg-[#111621] rounded-lg hover:bg-[#1E2532] transition-colors" data-testid="admin-link-customers">
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white">View Customers</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A1A7B5]" />
              </Link>
              <Link to="/admin/affiliates" className="flex items-center justify-between p-4 bg-[#111621] rounded-lg hover:bg-[#1E2532] transition-colors" data-testid="admin-link-affiliates">
                <div className="flex items-center space-x-3">
                  <TrendingUp className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white">Manage Affiliates</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A1A7B5]" />
              </Link>
              <Link to="/admin/emails" className="flex items-center justify-between p-4 bg-[#111621] rounded-lg hover:bg-[#1E2532] transition-colors" data-testid="admin-link-emails">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white">Email Lists</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A1A7B5]" />
              </Link>
              <Link to="/admin/promo-codes" className="flex items-center justify-between p-4 bg-[#111621] rounded-lg hover:bg-[#1E2532] transition-colors" data-testid="admin-link-promo-codes">
                <div className="flex items-center space-x-3">
                  <Tag className="w-5 h-5 text-[#00F0FF]" />
                  <span className="text-white">Promo Codes</span>
                </div>
                <ArrowRight className="w-5 h-5 text-[#A1A7B5]" />
              </Link>
            </div>

            {/* Top Products */}
            {data.top_products.length > 0 && (
              <div className="mt-6">
                <h3 className="text-white font-medium mb-3">Top Selling Products</h3>
                <div className="space-y-2">
                  {data.top_products.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-[#A1A7B5]">{p.name}</span>
                      <span className="text-white">{p.total_sold} sold</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
